/**
 * Custom hook for managing REST RAG chat streaming (Legal, TEBG, ITSM)
 * This is an unpublished work containing 3M confidential and proprietary information.
 * Disclosure, use or reproduction without the written authorization of 3M is prohibited.
 * If publication occurs, the following notice applies:
 * Copyright (C) 2025, 3M. All rights reserved.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { useDispatch } from 'react-redux';
import { setIsStreaming } from '../store/chatModeSlice';
import { RestRagChatService, RestRagResponseChunk, RestRagType } from '../utils/restRagChat.service';
import { ChatMessage } from '../utils/chat.service';
import logger from '../utils/logger';

export interface UseRestRagChatStreamOptions {
  ragType: RestRagType;
  onError?: (error: Error) => void;
  onComplete?: () => void;
}

export interface RestRagChatStreamState {
  isStreaming: boolean;
  error: Error | null;
  currentResponse: string;
  chatHistory: ChatMessage[];
  citations: any[];
}

export const useRestRagChatStream = ({ ragType, onError, onComplete }: UseRestRagChatStreamOptions) => {
  const { instance: msalInstance } = useMsal();
  const dispatch = useDispatch();
  const ragServiceRef = useRef<RestRagChatService | null>(null);
  const currentResponseRef = useRef<string>('');
  const citationsRef = useRef<any[]>([]);
  const waitingForCitationsRef = useRef<boolean>(false);
  const pendingContentRef = useRef<string>('');
  // Queue-based streaming: all incoming text chunks are pushed here; processQueue drains
  // one per rAF so the browser can paint each individually (same pattern as useChatStream).
  const chunkQueueRef = useRef<string[]>([]);
  const isProcessingQueueRef = useRef<boolean>(false);
  const pendingFinalizeRef = useRef<(() => void) | null>(null); // deferred finalization
  // displayAccRef tracks what the drain loop has rendered so far — separate from
  // currentResponseRef (which holds the full accumulated text used for finalization).
  const displayAccRef = useRef<string>('');
  // Stable rAF drain loop ref — assigned once via processQueueInitRef guard.
  const processQueueRef = useRef<() => void>(() => {});
  // Debug logging refs
  const firstChunkLoggedRef = useRef<boolean>(false);
  const chunkCountRef = useRef<number>(0);
  const lastChunkContentRef = useRef<string>('');

  const [state, setState] = useState<RestRagChatStreamState>({
    isStreaming: false,
    error: null,
    currentResponse: '',
    chatHistory: [],
    citations: [],
  });

  // Sync local isStreaming state to Redux so Header guards are always current
  useEffect(() => {
    dispatch(setIsStreaming(state.isStreaming));
  }, [state.isStreaming, dispatch]);

  // Stable rAF drain loop — assigned once (guarded by a companion ref) so the same
  // function identity is used for every requestAnimationFrame call across the lifetime
  // of this hook instance. Reads all refs directly so it never captures a stale closure.
  // Drains ONE item per rAF frame so the browser can paint each chunk individually.
  // When many items are backed up, drops stale intermediates and shows the latest
  // snapshot — one per frame — to keep visible motion without a multi-second backlog.
  //
  // Background-tab support: browsers pause requestAnimationFrame when the page is
  // not visible, but the underlying fetch/SSE reader continues on the network thread.
  // To prevent an ever-growing queue backlog (and blocked finalization) when the user
  // switches tabs, the loop detects document.visibilityState === 'hidden' and flushes
  // everything synchronously without any setState call (no rendering needed). A
  // companion visibilitychange effect below pushes the accumulated content back into
  // React state as soon as the user returns to the tab.
  const processQueueInitRef = useRef(false);
  if (!processQueueInitRef.current) {
    processQueueInitRef.current = true;
    const drainQueue = () => {
      if (chunkQueueRef.current.length === 0) {
        isProcessingQueueRef.current = false;
        const pending = pendingFinalizeRef.current;
        if (pending) {
          pendingFinalizeRef.current = null;
          pending();
        }
        return;
      }

      // When the page is hidden (background tab) rAF stops firing, causing the
      // queue to grow indefinitely and blocking finalization. Flush synchronously
      // instead: sync displayAccRef to the fully-received currentResponseRef,
      // clear the queue, and fire any pending finalization immediately.
      // The visibilitychange effect will push the result to React state on return.
      if (document.visibilityState === 'hidden') {
        displayAccRef.current = currentResponseRef.current;
        chunkQueueRef.current = [];
        isProcessingQueueRef.current = false;
        const pending = pendingFinalizeRef.current;
        if (pending) {
          pendingFinalizeRef.current = null;
          pending();
        }
        return;
      }

      // Take the next raw delta token and append to the running display string.
      // This guarantees N tokens = N rAF frames = N visible paints, regardless
      // of whether all tokens arrived in the same synchronous JS turn.
      const token = chunkQueueRef.current.shift()!;
      displayAccRef.current += token;
      setState(prev => ({ ...prev, currentResponse: displayAccRef.current }));
      requestAnimationFrame(drainQueue);
    };
    processQueueRef.current = drainQueue;
  }

  // Helper function to process content with citations and finalize message
  const finalizeMessageWithCitations = useCallback((content: string, citations: any[]) => {
    let processedContent = content;
    
    // Helper function to normalize URLs based on RAG type
    const normalizeUrl = (url: string): string => {
      if (!url) return '#';
      
      let workingUrl = url;
      
      // If it's already a proper absolute URL (not localhost), return as-is
      if ((workingUrl.startsWith('http://') || workingUrl.startsWith('https://')) && !workingUrl.includes('localhost')) {
        return workingUrl;
      }
      
      // Strip localhost if present (e.g., http://localhost:3000/3M-Cloud/...)
      if (workingUrl.includes('localhost')) {
        const localhostPattern = /^https?:\/\/localhost:\d+\//;
        workingUrl = workingUrl.replace(localhostPattern, '');
      }
      
      // Handle relative URLs and strip repo-specific prefixes based on RAG type
      let cleanUrl = workingUrl.startsWith('../') ? workingUrl.substring(3) : workingUrl.startsWith('./') ? workingUrl.substring(2) : workingUrl;
      
      switch (ragType) {
        case 'DevOps':
          // Strip the repo path if it's already in the URL
          cleanUrl = cleanUrl.replace(/^3M-Cloud\/devops\.mmm\.com-cfndist-modern\/docs\//, '');
          return `https://github.com/3M-Cloud/devops.mmm.com-cfndist-modern/blob/main/docs/${cleanUrl}`;
        case 'Legal':
          // TODO: Update with correct Legal RAG base URL when available
          cleanUrl = cleanUrl.replace(/^3M-Cloud\/legal-rag\//, '');
          return `https://github.com/3M-Cloud/legal-rag/blob/main/${cleanUrl}`;
        case 'TEBG':
          // TODO: Update with correct TEBG RAG base URL when available
          cleanUrl = cleanUrl.replace(/^3M-Cloud\/tebg-rag\//, '');
          return `https://github.com/3M-Cloud/tebg-rag/blob/main/${cleanUrl}`;
        case 'ITSM':
          // TODO: Update with correct ITSM RAG base URL when available
          cleanUrl = cleanUrl.replace(/^3M-Cloud\/itsm-rag\//, '');
          return `https://github.com/3M-Cloud/itsm-rag/blob/main/${cleanUrl}`;
        default:
          // Fallback: return as-is
          return workingUrl;
      }
    };
    
    // Helper function to extract a meaningful title from citation path
    const extractTitle = (citation: any): string => {
      // Prefer the title field if the backend provides it (e.g. Legal RAG)
      if (citation.title) return citation.title;
      
      const source = citation.source || '';
      const pathParts = source.split('/');
      
      // Look for the most meaningful part of the path
      // Priority: directory name before index.md > filename > last directory
      for (let i = pathParts.length - 1; i >= 0; i--) {
        const part = pathParts[i];
        if (part === 'index.md') {
          // Use the directory name before index.md
          if (i > 0) {
            const dirName = pathParts[i - 1];
            return dirName.replace(/-/g, ' ');
          }
        } else if (part.endsWith('.md')) {
          // Use the filename without extension
          return part.replace(/\.md$/, '').replace(/-/g, ' ');
        }
      }
      
      // Fallback to last directory
      const lastPart = pathParts[pathParts.length - 1] || 'Document';
      return lastPart.replace(/\.md$/, '').replace(/-/g, ' ');
    };
    
    // After processing, convert ALL remaining markdown links to HTML
    processedContent = processedContent.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (fullMatch, title, url) => {
      const cleanUrl = normalizeUrl(url.trim());
      return `<a href="${cleanUrl}" target="_blank" rel="noopener noreferrer">${title}</a>`;
    });
    
    // Convert DevOps citations to SourcesList format
    logger.log('🔵 [Legal/DevOps] Raw citations received:', citations);
    const sources = citations.length > 0 ? citations.map((citation: any) => {
      const title = extractTitle(citation);
      const normalizedUrl = normalizeUrl(citation.url || '');
      logger.log('🔵 [Legal/DevOps] Source —', 'title:', title, '| url:', normalizedUrl);
      return {
        url: normalizedUrl,
        title: title,
        snippet: citation.source
      };
    }) : undefined;
    logger.log('🔵 [Legal/DevOps] Total sources extracted:', sources?.length ?? 0);
    
    const assistantMessage: ChatMessage = {
      id: `${Date.now()}-${Math.random()}`,
      role: 'assistant',
      content: processedContent,
      citations: citations.length > 0 ? citations.map((c: any) => ({
        ...c,
        url: normalizeUrl(c.url || '')
      })) : undefined,
      sources: sources,
    };
    
    setState(prev => ({
      ...prev,
      isStreaming: false,
      currentResponse: '',
      chatHistory: [...prev.chatHistory, assistantMessage],
      citations: [],
    }));
    
    currentResponseRef.current = '';
    citationsRef.current = [];
    waitingForCitationsRef.current = false;
    pendingContentRef.current = '';
    chunkQueueRef.current = [];
    displayAccRef.current = '';
    isProcessingQueueRef.current = false;
    pendingFinalizeRef.current = null;
    
    if (onComplete) {
      onComplete();
    }
  }, [onComplete, ragType]);

  // Initialize REST RAG service
  const getRagService = useCallback(() => {
    if (!ragServiceRef.current) {
      ragServiceRef.current = new RestRagChatService(ragType, msalInstance);
      
      // Set up callbacks
      ragServiceRef.current.onMessage((chunk: RestRagResponseChunk) => {
        // Handle DevOps/Legal-style chunk types
        // Both DevOps and Legal send objects with keys like 'error', 'status', 'citations' instead of type/data structure
        const anyChunk = chunk as any;
        
        if ('error' in anyChunk && anyChunk.error) {
          const errorMsg = String(anyChunk.error);
          
          // Show error message to user
          const errorMessage: ChatMessage = {
            id: `${Date.now()}-${Math.random()}`,
            role: 'assistant',
            content: `Error: ${errorMsg}`,
            isError: true,
          };
          
          chunkQueueRef.current = [];
          isProcessingQueueRef.current = false;
          pendingFinalizeRef.current = null;
          
          setState(prev => ({
            ...prev,
            isStreaming: false,
            currentResponse: '',
            chatHistory: [...prev.chatHistory, errorMessage],
            error: new Error(errorMsg),
          }));
          
          currentResponseRef.current = '';
          citationsRef.current = [];
          
          if (onError) {
            onError(new Error(errorMsg));
          }
          return;
        }
        
        // Handle DevOps citations (separate from content)
        if ('citations' in anyChunk && Array.isArray(anyChunk.citations)) {
          citationsRef.current = anyChunk.citations;
          setState(prev => ({
            ...prev,
            citations: anyChunk.citations as any[],
          }));
          
          if (waitingForCitationsRef.current && pendingContentRef.current) {
            finalizeMessageWithCitations(pendingContentRef.current, anyChunk.citations);
          }
          return;
        }
        
        // Handle metadata/status updates
        if ('status' in chunk) {
          return;
        }
        
        if (chunk.type === 'content') {
          // Check for special markers - ensure content exists and is a string
          const content = chunk.data?.content || '';
          
          if (content === '__STREAM_START__') {
            currentResponseRef.current = '';
            citationsRef.current = [];
            chunkQueueRef.current = [];
            displayAccRef.current = '';
            isProcessingQueueRef.current = false;
            
            setState(prev => ({
              ...prev,
              isStreaming: true,
              currentResponse: '',
              citations: [],
            }));
          } else if (content.startsWith('__COMPLETE__')) {
            if (currentResponseRef.current) {
              // Process content to extract and format sources
              let processedContent = currentResponseRef.current;
              let sourcesForList: Array<{url: string; title: string; snippet?: string}> | undefined = undefined;
              
            // FIRST: Handle inline source citations
            // DevOps RAG sends citations both inline like *(Source: path/to/file.md)* and as citation objects
            if (citationsRef.current.length > 0) {
              const citationMap = new Map<string, any>();
              citationsRef.current.forEach((citation: any) => {
                if (citation.source && citation.url) {
                  citationMap.set(citation.source.toLowerCase(), citation);
                }
              });
              
              const usedCitations = new Set<string>();
              
              processedContent = processedContent.replace(/\*?\(?\*?Source:\s*([^)]*?)\)?\*?/gi, (match, sourcePath) => {
                const cleanPath = sourcePath.trim();
                const pathLower = cleanPath.toLowerCase();
                const citation = citationMap.get(pathLower);
                
                if (citation) {
                  usedCitations.add(pathLower);
                  const pathParts = cleanPath.split('/');
                  const lastPart = pathParts[pathParts.length - 2] || pathParts[pathParts.length - 1];
                  const title = lastPart.replace(/\.md$/, '').replace(/-/g, ' ');
                  return `<a href="${citation.url}" target="_blank" rel="noopener noreferrer" class="source-citation" title="${cleanPath}">${title}</a>`;
                } else {
                  return `*(Source: ${cleanPath})*`;
                }
              });
              
              // Add any unused citations at the bottom
              const unusedCitations = citationsRef.current.filter((citation: any) => 
                !usedCitations.has(citation.source.toLowerCase())
              );
              
              if (unusedCitations.length > 0) {
                let sourcesSection = '\n\n---\n\n**Additional Sources:**\n\n';
                unusedCitations.forEach((citation: any, idx: number) => {
                  let title = citation.title;
                  if (!title) {
                    const pathParts = citation.source.split('/');
                    const lastPart = pathParts[pathParts.length - 2] || pathParts[pathParts.length - 1];
                    title = lastPart.replace(/\.md$/, '').replace(/-/g, ' ');
                  }
                  
                  sourcesSection += `${idx + 1}. <a href="${citation.url}" target="_blank" rel="noopener noreferrer">${title}</a>\n`;
                });
                
                processedContent += sourcesSection;
              }
            }              // SECOND: Check if content contains a JSON array of sources (typically at the end)
              // Pattern: [...][{"title": "...", "source_url": "..."}...]
              const jsonArrayPattern = /\[{[^}]*"title"[^}]*"source_url"[^}]*}[^\]]*\]$/;
              const match = processedContent.match(jsonArrayPattern);
              
              if (match) {
                try {
                  const sourcesJson = JSON.parse(match[0]);
                  
                  if (Array.isArray(sourcesJson) && sourcesJson.length > 0) {
                    // Remove the JSON array from content
                    processedContent = processedContent.substring(0, match.index).trim();
                    
                    // Filter out duplicates based on source_url
                    const uniqueSources = Array.from(
                      new Map(sourcesJson.map((item: any) => [item.source_url, item])).values()
                    );
                    
                    sourcesForList = uniqueSources.map((source: any) => ({
                      url: source.source_url?.trim() || '#',
                      title: source.title || 'Untitled',
                      snippet: undefined
                    }));
                  }
                } catch (parseError) {
                  // Keep original content if parsing fails
                }
              }
              
              // After processing sources, convert ALL remaining markdown links to HTML
              // This fixes any links in the main content that have spaces or special characters
              processedContent = processedContent.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (fullMatch, title, url) => {
                // URLs are already properly encoded from backend, use as-is
                const cleanUrl = url.trim();
                return `<a href="${cleanUrl}" target="_blank" rel="noopener noreferrer">${title}</a>`;
              });
              
              // Prepare sources for SourcesList component
              // Priority: Use sourcesForList if available (from JSON array), otherwise use citations
              const sources = typeof sourcesForList !== 'undefined' ? sourcesForList : 
                (citationsRef.current.length > 0 ? citationsRef.current.map((citation: any) => {
                  // Prefer the title field if the backend provides it (e.g. Legal RAG),
                  // otherwise fall back to extracting from the source file path.
                  let title = citation.title;
                  if (!title) {
                    const pathParts = citation.source?.split('/') || [];
                    const fileName = pathParts[pathParts.length - 1] || 'Document';
                    title = fileName.replace(/\.md$/, '').replace(/-/g, ' ');
                  }
                  
                  return {
                    url: citation.url || '#',
                    title: title
                    // snippet removed for RAG sources - URL text hidden
                  };
                }) : undefined);
              
              const assistantMessage: ChatMessage = {
                id: `${Date.now()}-${Math.random()}`,
                role: 'assistant',
                content: processedContent,
                citations: citationsRef.current.length > 0 ? citationsRef.current : undefined,
                sources: sources,
              };
              
              setState(prev => ({
                ...prev,
                isStreaming: false,
                currentResponse: '',
                chatHistory: [...prev.chatHistory, assistantMessage],
                citations: [],
              }));
              
              currentResponseRef.current = '';
              citationsRef.current = [];
              chunkQueueRef.current = [];
              isProcessingQueueRef.current = false;
              pendingFinalizeRef.current = null;
            } else {
              // No content accumulated - nothing to save
            }
            
            if (onComplete) {
              onComplete();
            }
          } else {
            // Regular content chunk — push raw text onto the queue, then start the
            // processQueue loop if it isn't already running.  The loop drains one
            // snapshot per rAF so every frame the browser actually paints progress.
            chunkCountRef.current += 1;
            lastChunkContentRef.current = chunk.data.content;
            if (!firstChunkLoggedRef.current) {
              firstChunkLoggedRef.current = true;
            }
            
            currentResponseRef.current += chunk.data.content;
            
            // Push raw delta token onto the queue — the drain loop appends
            // each token to its own running accumulator one frame at a time.
            chunkQueueRef.current.push(chunk.data.content);
            
            // Kick off the drain loop if it isn't already running.
            // processQueueRef.current is the stable hook-level function that
            // drains one snapshot per rAF and defers finalization until empty.
            if (!isProcessingQueueRef.current) {
              isProcessingQueueRef.current = true;
              requestAnimationFrame(processQueueRef.current);
            }
          }
        } else if (chunk.type === 'done') {          
          const finalizeDone = () => {
            if (currentResponseRef.current) {
              if (citationsRef.current.length > 0) {
                finalizeMessageWithCitations(currentResponseRef.current, citationsRef.current);
              } else {
                waitingForCitationsRef.current = true;
                pendingContentRef.current = currentResponseRef.current;
                
                setTimeout(() => {
                  if (waitingForCitationsRef.current && pendingContentRef.current) {
                    finalizeMessageWithCitations(pendingContentRef.current, []);
                  }
                }, 2000);
              }
            } else {
              setState(prev => ({
                ...prev,
                isStreaming: false,
                currentResponse: '',
              }));
              
              if (onComplete) {
                onComplete();
              }
            }
          };
          
          if (isProcessingQueueRef.current) {
            pendingFinalizeRef.current = finalizeDone;
          } else finalizeDone();

        } else if (chunk.type === 'citations') {
          try {
            const citations = JSON.parse(chunk.data.content);
            citationsRef.current = citations;
            setState(prev => ({
              ...prev,
              citations: citations,
            }));
          } catch (error) {
            // Ignore malformed citations
          }
        }
      });
      
      ragServiceRef.current.onError((error: Error) => {
        chunkQueueRef.current = [];
        isProcessingQueueRef.current = false;
        pendingFinalizeRef.current = null;
        setState(prev => ({
          ...prev,
          isStreaming: false,
          // Don't set error state for AbortError as it's expected when user stops stream
          error: error.name === 'AbortError' ? null : error,
        }));
        
        // Don't call onError for AbortError as it's expected when user stops stream
        if (onError && error.name !== 'AbortError') {
          onError(error);
        }
      });
      
      ragServiceRef.current.onComplete(() => {
        // Fires once when the stream fully ends (guarded against double-fire in the service).        
        const finalizeOnComplete = () => {
          if (currentResponseRef.current) {
            if (citationsRef.current.length > 0) {
              finalizeMessageWithCitations(currentResponseRef.current, citationsRef.current);
            } else {
              waitingForCitationsRef.current = true;
              pendingContentRef.current = currentResponseRef.current;
              
              // Fallback: finalize without citations after 2 seconds
              setTimeout(() => {
                if (waitingForCitationsRef.current && pendingContentRef.current) {
                  finalizeMessageWithCitations(pendingContentRef.current, []);
                }
              }, 2000);
            }
          } else {
            setState(prev => ({
              ...prev,
              isStreaming: false,
              currentResponse: '',
            }));
            
            if (onComplete) {
              onComplete();
            }
          }
        };
        
        if (isProcessingQueueRef.current) {
          pendingFinalizeRef.current = finalizeOnComplete;
        } else {
          finalizeOnComplete();
        }
      });
    }
    
    return ragServiceRef.current;
  }, [ragType, msalInstance, onError, finalizeMessageWithCitations, onComplete]);

  // Send a message
  const sendMessage = useCallback(async (message: string) => {
    try {
      // Add user message to history and start streaming immediately
      const userMessage: ChatMessage = {
        id: `${Date.now()}-${Math.random()}`,
        role: 'user',
        content: message,
      };
      
      // Capture the current state before updating
      const currentState = state;
      
      // Add user message to the chat history
      const updatedChatHistory = [...currentState.chatHistory, userMessage];
      
      firstChunkLoggedRef.current = false;
      chunkCountRef.current = 0;
      lastChunkContentRef.current = '';
      displayAccRef.current = '';
      chunkQueueRef.current = [];
      isProcessingQueueRef.current = false;
      pendingFinalizeRef.current = null;
      setState(prev => ({
        ...prev,
        chatHistory: updatedChatHistory,
        error: null,
        isStreaming: true, // Set streaming to true immediately to show loading indicator
      }));
      
      // Convert chat history to the format expected by the RAG service
      // Handle both string and ChatContentItem[] content types
      const chatHistoryPayload = updatedChatHistory.map(msg => ({
        role: msg.role,
        content: typeof msg.content === 'string' 
          ? msg.content 
          : msg.content.map(item => 
              item.type === 'input_text' ? item.text : `[${item.type}]`
            ).join(' ')
      }));
      
      // Get or create service and send query with full chat history
      const service = getRagService();
      await service.sendQuery(message, chatHistoryPayload);
      
    } catch (error: any) {
      const errorInstance = error instanceof Error ? error : new Error('Unknown error');
      const isAbort = errorInstance.name === 'AbortError' || errorInstance.message.includes('aborted');
      
      setState(prev => ({
        ...prev,
        isStreaming: false,
        // Don't set error state for AbortError as it's expected when user stops stream
        error: isAbort ? null : errorInstance,
      }));
      
      // Don't call onError for AbortError as it's expected when user stops stream
      if (onError && !isAbort) {
        onError(error);
      }
    }
  }, [getRagService, onError, state]);

  // Clear chat history
  const clearHistory = useCallback(() => {
    setState(prev => ({
      ...prev,
      chatHistory: [],
      currentResponse: '',
      citations: [],
      error: null,
    }));
    
    currentResponseRef.current = '';
    citationsRef.current = [];
  }, []);

  // Cancel current request
  const cancelRequest = useCallback(() => {
    if (ragServiceRef.current) {
      ragServiceRef.current.cancel();
      
      // Get the current streamed content before clearing
      const preservedContent = currentResponseRef.current || '';
      
      // Add stopped message to chat history if there was any content
      const stoppedMessage = preservedContent 
        ? preservedContent + '\n\n*[Response was stopped by user.]*'
        : '*[Response was stopped by user.]*';
      
      setState(prev => {
        // Find the last user message to store as context for retry
        const lastUserMessage = [...prev.chatHistory].reverse().find(m => m.role === 'user');
        
        return {
          ...prev,
          isStreaming: false,
          currentResponse: '', // Clear current response since we're moving it to chat history
          chatHistory: [...prev.chatHistory, {
            id: `${Date.now()}-${Math.random()}`,
            role: 'assistant',
            content: stoppedMessage,
            isStopped: true,
            failedUserMessage: lastUserMessage
          }],
        };
      });
      
      currentResponseRef.current = '';
    }
  }, []);

  // Sync display state when the user returns to a tab that was hidden during streaming.
  // While background, the rAF drain loop flushes refs synchronously without calling
  // setState. This effect handles the visibility transition back to 'visible' so the
  // component shows the latest accumulated content and, if the stream finished in the
  // background, the final message is already in chatHistory (finalization ran in the
  // flush). If the stream is still in progress, we restart the drain loop so the
  // animation continues from the current position.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;

      // Push any content received while hidden into React state.
      if (currentResponseRef.current && currentResponseRef.current !== displayAccRef.current) {
        displayAccRef.current = currentResponseRef.current;
        setState(prev => {
          if (!prev.isStreaming) return prev;
          return { ...prev, currentResponse: displayAccRef.current };
        });
      }

      // Restart the drain loop if new chunks have arrived since the flush.
      if (chunkQueueRef.current.length > 0 && !isProcessingQueueRef.current) {
        isProcessingQueueRef.current = true;
        requestAnimationFrame(processQueueRef.current);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []); // reads only refs — no reactive deps needed

  // Cleanup on unmount
  const dispose = useCallback(() => {
    if (ragServiceRef.current) {
      ragServiceRef.current.dispose();
      ragServiceRef.current = null;
    }
  }, []);

  // Set chat history (for restoring from Redux)
  const setChatHistory = useCallback((history: ChatMessage[]) => {
    setState(prev => ({
      ...prev,
      chatHistory: history,
    }));
  }, []);

  return {
    ...state,
    sendMessage,
    clearHistory,
    cancelRequest,
    dispose,
    setChatHistory,
  };
};
